import styled from '@emotion/styled';
import { css } from '@emotion/core';
import React from 'react';
import {
  BREAKPOINT_SMALL,
  COLOR_DARK_PINK,
  COLOR_DARK_PURPLE,
} from '../../../../components/MarketingPagesShared/styles';
import { CDN_PATH } from '../../constants';
import { SwitchPageSection } from '../../styles';

export function SwitchReasonsSection() {
  return (
    <SwitchReasonsDesktopBackground>
      <SwitchReasonsMobileBackground>
        <SwitchPageSection>
          <SwitchReasonsDualColumns>
            <SwitchReasonsWhyTitle>Why switch?</SwitchReasonsWhyTitle>
            <SwitchReasonsList>
              <SwitchReasonsListItem>
                <SwitchReasonsListHeader>
                  Get unparalleled insights, powered by Spotify
                </SwitchReasonsListHeader>
                <SwitchReasonsListText>
                  Improve your content using analytics from all platforms and
                  exclusive insights like episode dropoff and listener
                  demographics from Spotify.
                </SwitchReasonsListText>
              </SwitchReasonsListItem>
              <SwitchReasonsListItem>
                <SwitchReasonsListHeader>
                  Earn more with free hosting and monetization
                </SwitchReasonsListHeader>
                <SwitchReasonsListText>
                  Upload and store unlimited episodes for free, and unlock
                  sponsorships to feature audio ads—read by you—so you can earn
                  money on your podcast.
                </SwitchReasonsListText>
              </SwitchReasonsListItem>
              <SwitchReasonsListItem>
                <SwitchReasonsListHeader>
                  Focus more on creating, less on logistics
                </SwitchReasonsListHeader>
                <SwitchReasonsListText>
                  We can help with everything—from distribution to audience
                  growth and monetization. Plus, get ongoing support whenever
                  you need it.
                </SwitchReasonsListText>
              </SwitchReasonsListItem>
              <SwitchReasonsListItem>
                <SwitchReasonsListHeader>
                  You control your podcast
                </SwitchReasonsListHeader>
                <SwitchReasonsListText>
                  You own all of your content, always. During the switch, your
                  podcast won’t experience any downtime, and you’ll keep all of
                  your subscribers in the process.
                </SwitchReasonsListText>
              </SwitchReasonsListItem>
            </SwitchReasonsList>
          </SwitchReasonsDualColumns>
        </SwitchPageSection>
      </SwitchReasonsMobileBackground>

      <SwitchPageSection>
        <SwitchReasonsDifferenceTitle>
          You’ll notice a difference, but your listeners won’t notice a thing
        </SwitchReasonsDifferenceTitle>
        <SwitchReasonsListSecondary>
          <li>
            <SwitchReasonsListSecondaryHeader>
              Import your episodes
            </SwitchReasonsListSecondaryHeader>
            <SwitchReasonsListSecondaryText>
              Simply paste in your RSS feed, and we’ll import all of your
              episodes and metadata—no manual uploading required.
            </SwitchReasonsListSecondaryText>
          </li>
          <li>
            <SwitchReasonsListSecondaryHeader>
              Redirect your feed
            </SwitchReasonsListSecondaryHeader>
            <SwitchReasonsListSecondaryText>
              Redirecting will let your listeners hear new episodes, without
              affecting your subscribers on listening apps.
            </SwitchReasonsListSecondaryText>
          </li>
          <li>
            <SwitchReasonsListSecondaryHeader>
              Start creating
            </SwitchReasonsListSecondaryHeader>
            <SwitchReasonsListSecondaryText>
              Draft new episodes by uploading audio or using our built-in
              creation tools.
            </SwitchReasonsListSecondaryText>
          </li>
        </SwitchReasonsListSecondary>
      </SwitchPageSection>
    </SwitchReasonsDesktopBackground>
  );
}

const backgroundImages = css`
  background-color: ${COLOR_DARK_PINK};
  background-image: url("${CDN_PATH}/bg-red.svg"), url("${CDN_PATH}/bg-purple.svg");
  background-repeat: repeat-x;
  // Chrome has a pixel-rounding error so sometimes the background doesn't
  // land on the bottom, this is a workaround for that
  background-position: left top, left calc(100% + 2px);
`;

const SwitchReasonsDesktopBackground = styled.section`
  @media (min-width: ${BREAKPOINT_SMALL}px) {
    ${backgroundImages};
    padding-top: 200px;
    padding-bottom: 200px;
  }
  @media (max-width: ${BREAKPOINT_SMALL}px) {
    background: ${COLOR_DARK_PURPLE};
    padding-top: 0;
    padding-bottom: 100px;
  }
`;

const SwitchReasonsMobileBackground = styled.section`
  @media (max-width: ${BREAKPOINT_SMALL}px) {
    ${backgroundImages};
    background-size: 1000px auto;
  }
`;

const SwitchReasonsWhyTitle = styled.h2`
  font-size: 3.6rem;
  font-weight: 800;
  margin: 0;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    font-size: 2.4rem;
    font-weight: bold;
  }
`;

const SwitchReasonsDifferenceTitle = styled.h2`
  font-size: 6.4rem;
  line-height: 1.2;
  font-weight: 800;
  width: 80%;
  margin: 200px 0 100px 0;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    margin: 50px 0;
    width: auto;
    font-size: 4rem;
  }
`;

const SwitchReasonsDualColumns = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  grid-gap: 60px;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    grid-template-columns: 1fr;
    grid-gap: 30px;
  }
`;

const SwitchReasonsList = styled.ul`
  border-top: 4px solid white;
  list-style-type: none;
  padding: 25px 0 0;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    border-top: 2px solid white;
  }
`;

const SwitchReasonsListItem = styled.li`
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0 0 50px 0;
  margin: 0 0 50px 0;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    &:last-of-type {
      border-bottom: none;
    }
  }
`;

const SwitchReasonsListHeader = styled.h3`
  font-size: 3.6rem;
  font-weight: bold;
  line-height: 1.2;
  margin: 0 0 20px 0;
`;

const SwitchReasonsListText = styled.p`
  color: white;
  font-size: 2.2rem;
  line-height: 1.4;
`;

const SwitchReasonsListSecondary = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 60px;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    grid-template-columns: 1fr;
    grid-gap: 45px;
  }
`;

const SwitchReasonsListSecondaryHeader = styled.h3`
  font-size: 2rem;
  line-height: 1.2;
  font-weight: bold;
  border-bottom: 2px solid white;
  margin: 0 0 15px 0;
  padding: 0 0 15px 0;
`;

const SwitchReasonsListSecondaryText = styled.p`
  font-weight: normal;
  font-size: 2rem;
  line-height: 1.4;
  color: white;
`;
